import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";

import { getCurrentUser, signIn, signOut } from "./auth";

import type { SignInResult } from "./auth";

import type { CloudUser } from "./types";

type BindHandler = (
  key: string,
  etag: string | null,
  opts?: { flushPrevious?: boolean },
) => Promise<void>;
type UnbindHandler = () => Promise<void>;

interface CloudContextValue {
  user: CloudUser | null;
  isLoading: boolean; // true only during initial session restore
  error: string | null;
  login: (username: string, password: string) => Promise<SignInResult>;
  completeSignIn: (user: CloudUser) => void;
  logout: () => void;
  clearError: () => void;
  // Current open file tracking
  currentKey: string | null;
  setCurrentKey: (key: string | null) => void;
  currentFilename: string | null;
  setCurrentFilename: (name: string | null) => void;
  // Autosave binding — App.tsx registers handlers; consumers call them when
  // they swap the loaded file so the autosave hook can flush pending writes
  // for the previous file and rebind to the new key + etag.
  bindFile: BindHandler;
  unbindFile: UnbindHandler;
  registerBindHandlers: (bind: BindHandler, unbind: UnbindHandler) => void;
  // Save dialog
  saveDialogOpen: boolean;
  saveDialogPrefix: string | undefined;
  openSaveDialog: (prefix?: string) => void;
  closeSaveDialog: () => void;
  // Open dialog
  openDialogOpen: boolean;
  openOpenDialog: () => void;
  closeOpenDialog: () => void;
}

const CloudContext = createContext<CloudContextValue>({
  user: null,
  isLoading: true,
  error: null,
  login: async () => ({ type: "success" as const, user: null as any }),
  completeSignIn: () => {},
  logout: () => {},
  clearError: () => {},
  currentKey: null,
  setCurrentKey: () => {},
  currentFilename: null,
  setCurrentFilename: () => {},
  bindFile: async () => {},
  unbindFile: async () => {},
  registerBindHandlers: () => {},
  saveDialogOpen: false,
  saveDialogPrefix: undefined,
  openSaveDialog: () => {},
  closeSaveDialog: () => {},
  openDialogOpen: false,
  openOpenDialog: () => {},
  closeOpenDialog: () => {},
});

export const CloudProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<CloudUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentKey, setCurrentKey] = useState<string | null>(null);
  const [currentFilename, setCurrentFilename] = useState<string | null>(null);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveDialogPrefix, setSaveDialogPrefix] = useState<
    string | undefined
  >();
  const [openDialogOpen, setOpenDialogOpen] = useState(false);

  // Autosave bind handlers — installed by App.tsx via registerBindHandlers.
  // Refs (not state) so registration doesn't trigger re-renders.
  const bindHandlerRef = useRef<BindHandler>(async () => {});
  const unbindHandlerRef = useRef<UnbindHandler>(async () => {});

  const registerBindHandlers = useCallback(
    (bind: BindHandler, unbind: UnbindHandler) => {
      bindHandlerRef.current = bind;
      unbindHandlerRef.current = unbind;
    },
    [],
  );

  const bindFile = useCallback<BindHandler>(
    (key, etag, opts) => bindHandlerRef.current(key, etag, opts),
    [],
  );

  const unbindFile = useCallback<UnbindHandler>(
    () => unbindHandlerRef.current(),
    [],
  );

  const openSaveDialog = useCallback((prefix?: string) => {
    setSaveDialogPrefix(prefix);
    setSaveDialogOpen(true);
  }, []);

  const closeSaveDialog = useCallback(() => {
    setSaveDialogOpen(false);
    setSaveDialogPrefix(undefined);
  }, []);

  const openOpenDialog = useCallback(() => setOpenDialogOpen(true), []);
  const closeOpenDialog = useCallback(() => setOpenDialogOpen(false), []);

  useEffect(() => {
    getCurrentUser()
      .then((u) => setUser(u))
      .finally(() => setIsLoading(false));
  }, []);

  const login = useCallback(
    async (username: string, password: string): Promise<SignInResult> => {
      // NOTE: do NOT touch isLoading here — that's only for initial session
      // restore. LoginScreen manages its own busy state via the button spinner.
      setError(null);
      try {
        const result = await signIn(username, password);
        if (result.type === "success") {
          setUser(result.user);
        }
        return result;
      } catch (err: any) {
        setError(err?.message ?? "Login failed");
        throw err;
      }
    },
    [],
  );

  const completeSignIn = useCallback((u: CloudUser) => {
    setUser(u);
  }, []);

  const logout = useCallback(() => {
    // Best-effort flush of any pending autosave before tearing down credentials.
    unbindHandlerRef.current().catch(() => {});
    signOut();
    setUser(null);
    setCurrentKey(null);
    setCurrentFilename(null);
  }, []);

  const clearError = useCallback(() => setError(null), []);

  return (
    <CloudContext.Provider
      value={{
        user,
        isLoading,
        error,
        login,
        completeSignIn,
        logout,
        clearError,
        currentKey,
        setCurrentKey,
        currentFilename,
        setCurrentFilename,
        bindFile,
        unbindFile,
        registerBindHandlers,
        saveDialogOpen,
        saveDialogPrefix,
        openSaveDialog,
        closeSaveDialog,
        openDialogOpen,
        openOpenDialog,
        closeOpenDialog,
      }}
    >
      {children}
    </CloudContext.Provider>
  );
};

export const useCloud = () => useContext(CloudContext);
